import { type Cell, flexRender, type Row } from "@tanstack/react-table";

import { Checkbox } from "@vega-core/components/shadcn/checkbox";

import { MobileTableHeaderCell } from "./shared/MobileTableHeaderCell";

type MobileTableRowHeaderProps<TData> = {
  row: Row<TData>;
  headerCells: Cell<TData, unknown>[];
  actionCell: Cell<TData, unknown> | undefined;
  enableRowSelection: boolean;
  isSelected: boolean;
  canSelect: boolean;
  cellClassName?: string;
  hideUnselectableCheckboxes?: boolean;
};

export function MobileTableRowHeader<TData>({
  row,
  headerCells,
  actionCell,
  enableRowSelection,
  isSelected,
  canSelect,
  cellClassName,
  hideUnselectableCheckboxes = false,
}: MobileTableRowHeaderProps<TData>) {
  return (
    <div className="bg-background/subtle p-4">
      <div className="flex flex-row flex-wrap gap-2">
        <div className="flex items-center w-full">
          {enableRowSelection && !(hideUnselectableCheckboxes && !canSelect) ? (
            <div
              className="mr-3"
              onClick={(e) => {
                e.stopPropagation();

                if (canSelect) {
                  row.toggleSelected(!isSelected);
                }
              }}
            >
              <Checkbox
                checked={isSelected}
                className="flex items-center justify-center"
                data-testid={`mobile-checkbox-${row.id}`}
                disabled={!canSelect}
              />
            </div>
          ) : null}

          <div className="flex-1 flex items-center gap-2 min-w-0">
            {headerCells.map((cell) => (
              <MobileTableHeaderCell<TData>
                cell={cell}
                cellClassName={cellClassName}
                key={cell.id}
                rowId={row.id}
              />
            ))}
          </div>

          {actionCell ? (
            <div
              className="flex items-center gap-2 ml-auto shrink-0"
              data-interactive="true"
              data-testid={`mobile-action-${row.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {flexRender(
                actionCell.column.columnDef.cell,
                actionCell.getContext(),
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
